import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Highlight,
  Snippet,
  useHits,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { Pagination } from "semantic-ui-react";
import { getCallRecordingTranscriptions } from "../../api/transcriptions";
import { BUSY_CALL_STATUSES } from "../../hooks/useCalls";
import { useAuth } from "../../contexts/AuthContext";
import { DataTable, EmptyState, ErrorState, LoadingState } from "../ui";
import CallTranscriptionRow, { COLUMNS } from "./CallTranscriptionRow";

// A search hit in the shape of the API resource
function hitAsCall(hit, locale) {
  const when = (seconds) =>
    seconds
      ? new Date(seconds * 1000).toLocaleString(locale, {
          timeZone: hit.effective_timezone,
        })
      : null;
  return {
    sqid: hit.sqid,
    status: hit.status,
    // Records indexed before has_summary existed lack it
    has_summary: hit.has_summary ?? true,
    // The index stores just the rating
    sentiment: hit.sentiment_rating ? { rating: hit.sentiment_rating } : null,
    direction: hit.direction,
    tel_number: hit.tel_number,
    user_name: hit.user_name,
    user_ext: hit.user_ext,
    start_utc: hit.start_timestamp
      ? new Date(hit.start_timestamp * 1000).toISOString()
      : null,
    start: when(hit.start_timestamp),
    end: when(hit.end_timestamp),
    duration: hit.duration,
    effective_timezone: hit.effective_timezone,
  };
}

const CallsTable = ({ children }) => (
  <DataTable
    scroll
    rowGroups
    unstackable
    columns={COLUMNS.map((c) => ({ key: c, label: c }))}
  >
    {children}
  </DataTable>
);

const CallTranscriptions = ({ searchMode, searchParams }) => {
  // A page belongs to the search it was picked in, so a new search is on
  // page 1 from its first render (no fetch for the old page number).
  const [pageState, setPageState] = useState({ page: 1, searchParams });
  const page = pageState.searchParams === searchParams ? pageState.page : 1;

  const queryClient = useQueryClient();
  const { apiUser } = useAuth();
  const { query } = useSearchBox();
  const hits = useHits();
  const { status: searchStatus, refresh } = useInstantSearch();

  const transcriptionsQuery = useQuery(
    ["call-transcriptions", page, searchParams],
    () => {
      return getCallRecordingTranscriptions("", {
        page: page,
        hydrate: "0",
        ...searchParams,
      });
    },
    {
      keepPreviousData: true,
      staleTime: 60000,
      refetchIntervalInBackground: true,
      refetchInterval: (data) => {
        let interval = false;
        data?.data.data.forEach((i) => {
          if (BUSY_CALL_STATUSES.includes(i?.status.value)) {
            queryClient.invalidateQueries({ queryKey: ["call-recordings"] });
            interval = 30 * 1000;
          }
        });
        return interval;
      },
    },
  );

  const handlePaginationChange = (e, { activePage }) => {
    setPageState({ page: activePage, searchParams });
  };

  if (transcriptionsQuery.isLoading) return <LoadingState />;

  const filtered = Object.values(searchParams ?? {}).some(Boolean);

  const list = () => {
    if (searchMode === "simple" && query.length > 0) {
      if (searchStatus === "error") {
        return (
          <ErrorState onRetry={refresh} retryLabel="Try again">
            Search failed.
          </ErrorState>
        );
      }
      const items = hits?.items ?? [];
      if (items.length === 0) {
        return <EmptyState inline>No calls match that search.</EmptyState>;
      }
      return (
        <CallsTable>
          {items.map((hit) => (
            <CallTranscriptionRow
              key={hit.sqid}
              call={hitAsCall(hit, apiUser.locale)}
              shown={{
                name: <Highlight attribute="user_name" hit={hit} />,
                number: <Highlight attribute="tel_number" hit={hit} />,
                extension: <Highlight attribute="user_ext" hit={hit} />,
                direction: <Highlight attribute="direction" hit={hit} />,
                summary: <Snippet attribute="transcription" hit={hit} />,
              }}
            />
          ))}
        </CallsTable>
      );
    }

    if (transcriptionsQuery.isError) {
      return (
        <ErrorState
          onRetry={() => transcriptionsQuery.refetch()}
          retryLabel="Try again"
        >
          Couldn't load your calls.
        </ErrorState>
      );
    }
    const calls = transcriptionsQuery.data.data.data;
    if (calls.length === 0) {
      return (
        <EmptyState inline>
          {filtered ? "No calls match that search." : "No processed calls yet."}
        </EmptyState>
      );
    }
    return (
      <>
        <CallsTable>
          {calls.map((c) => (
            <CallTranscriptionRow key={c.sqid} call={c} />
          ))}
        </CallsTable>
        <div style={{ width: "100%", textAlign: "center" }}>
          <Pagination
            inverted
            activePage={page}
            totalPages={transcriptionsQuery.data.data.meta.last_page}
            onPageChange={handlePaginationChange}
          />
        </div>
      </>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {list()}
    </div>
  );
};

export default CallTranscriptions;
